import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Win1 from './datasets/macro';
import Win2 from './datasets/earnings';


const StyledComp = styled.div`
  --panel-bg: rgb(245, 245, 244);
  --border-subtle: rgba(var(--blackr), 0.08);
  --radius-lg: 16px;
  --radius-md: 15px;
  --radius-pill: 999px;

  background: var(--white);
  color: var(--black);

  .wrap {
    max-width: 1200px;
    margin: 0 auto;
    padding: 72px 24px;
  }

  /* ---------- Header ---------- */
  .header {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 48px;
    align-items: start;
    margin-bottom: 56px;
  }

  .eyebrow {
    font-size: 15px;
    color: rgba(var(--blackr), 0.45);
    margin: 0 0 16px;
    font-weight: 500;
    letter-spacing: -0.01em;
  }

  .headline {
    font-size: clamp(34px, 4.2vw, 52px);
    line-height: 1.06;
    letter-spacing: -0.02em;
    margin: 0 0 32px;
  }

  .cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 20px;
    background-color: var(--black);
    color: var(--white);
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition-duration: 0.3s;

    &:hover {
      background-color: #333;
    }
  }

  .cta-btn svg {
    padding-left: 10px;
    transition-duration: 0.3s;
  }

  .cta-btn:hover svg {
    transform: translateX(2px);
  }

  .header-copy {
    font-size: 18px;
    line-height: 1.5;
    color: rgba(var(--blackr), 0.62);
    margin: 6px 0 0;
    max-width: 46ch;
  }

  /* ---------- Main feature grid (2 large cards) ---------- */
  .feature-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }

  .feature-card {
    position: relative;
    border-radius: var(--radius-lg);
    overflow: hidden;
    min-height: 640px;
    display: flex;
    flex-direction: column;
  }

  .feature-card--media {
    background: url('/product_images/3.webp');
    background-size: cover;
    justify-content: flex-end;
    color: var(--white);
  }

  .media{
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    >div{
      padding: 0 20px;
      width: 100%;
      @media (max-width: 900px) {
        padding: 20px;
      }
    }
  }

  .tag {
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: var(--radius-pill);
    background: rgba(var(--whiter), 0.18);
    color: var(--white);
    backdrop-filter: blur(6px);
    border: 1px solid rgba(var(--whiter), 0.25);
  }

  .icon {
    width: 56px;
    height: 56px;
    opacity: 0.5;
  }

  .feature-card--panel {
    background: var(--panel-bg);
    justify-content: flex-end;
  }

  .panel{
    flex: 1;
    margin: 20px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    >div{
      width: 100%;
    }
  }

  .panel-tag {
    background: rgba(var(--blackr), 0.06);
    color: rgba(var(--blackr), 0.5);
    border: 1px solid var(--border-subtle);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: var(--radius-pill);
  }

  .card-footer {
    padding: 28px 28px 32px;
  }

  .card-footer-label {
    font-size: 14px;
    font-weight: 500;
    margin: 0 0 10px;
    opacity: 0.7;
  }

  .feature-card--media .card-footer-label {
    color: rgba(var(--whiter), 0.85);
  }

  .feature-card--panel .card-footer-label {
    color: rgba(var(--blackr), 0.45);
  }

  .card-footer-copy {
    font-size: 17px;
    line-height: 1.45;
    margin: 0;
    max-width: 44ch;
  }

  .feature-card--media .card-footer-copy {
    color: rgba(var(--whiter), 0.95);
  }

  .feature-card--panel .card-footer-copy {
    color: rgba(var(--blackr), 0.85);
  }

  /* ---------- Bottom dynamic grid (2-4 items) ---------- */
  .info-grid {
    display: grid;
    grid-template-columns: repeat(var(--info-cols, 3), 1fr);
    gap: 20px;
  }

  .info-card {
    background: var(--panel-bg);
    border-radius: var(--radius-lg);
    padding: 28px 28px 32px;
    min-height: 260px;
    display: flex;
    flex-direction: column;
  }

  .info-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: var(--white);
    border: 1px solid var(--border-subtle);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: auto;

    svg {
      width: 18px;
      height: 18px;
      stroke: var(--black);
    }
  }

  .info-title {
    font-size: 15px;
    font-weight: 500;
    color: rgba(var(--blackr), 0.45);
    margin: 24px 0 10px;
  }

  .info-copy {
    font-size: 16px;
    line-height: 1.45;
    color: rgba(var(--blackr), 0.85);
    margin: 0;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 900px) {
    .header {
      grid-template-columns: 1fr;
      gap: 24px;
    }
    .feature-grid {
      grid-template-columns: 1fr;
    }
    .feature-card {
      min-height: 460px;
    }
    .info-grid {
      grid-template-columns: 1fr !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      transition: none !important;
    }
  }
`;

type InfoItem = {
  title: string;
  copy: string;
  icon: React.ReactNode;
};

const defaultInfoItems: InfoItem[] = [
  {
    title: 'Pools',
    copy: 'ETF holdings and index data, from constituents and weightings to historical performance.',
    icon: (
      <>
        <path d="M12 3l9 5-9 5-9-5 9-5z" />
        <path d="M3 13l9 5 9-5" />
      </>
    ),
  },
  {
    title: 'Market catalysts',
    copy: 'Economic calendar, breaking news and macro releases like CPI and unemployment.',
    icon: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18" />
      </>
    ),
  },
  {
    title: 'Ratings',
    copy: 'ESG scores and credit ratings to evaluate risk beyond the balance sheet.',
    icon: (
      <>
        <path d="M8 12V6a2 2 0 1 1 4 0v5" />
        <path d="M12 5.5V4a2 2 0 1 1 4 0v7" />
        <path d="M16 6.5V6a2 2 0 1 1 4 0v9a7 7 0 0 1-7 7h-1a7 7 0 0 1-6-3.4L4 12.5A1.7 1.7 0 0 1 6.9 11L8 12.6" />
      </>
    ),
  },
  {
    title: 'User & behavioral',
    copy: 'Sentiment and web traffic signals that show how markets and consumers are trending across daily and hourly history.',
    icon: (
      <>
        <path d="M3 12h4l2-7 4 14 2-7h6" />
      </>
    ),
  },
];

const Comp = ({
  headline = (
    <>
      One API for every
      <br />
      financial market
    </>
  ),
  ctaLabel = 'Get Started',
  description = 'AxionQuant is a single financial data API for market data, corporate intelligence, fund data, market catalysts, ratings and behavioral signals across every major asset class.',
  mediaCardLabel = 'Market Data',
  mediaCardCopy = 'Historical market data for equities, currencies, contracts, and macros',
  panelCardLabel = 'Corporate intelligence',
  panelCardCopy = 'Company profiles, earnings, SEC filings, financials and supply chain data.',
  infoItems = defaultInfoItems,
}) => {
  const navigate = useNavigate();
  const cols = Math.min(Math.max(infoItems.length, 2), 4);

  return (
    <StyledComp>
      <div className="wrap">
        {/* Header */}
        <div className="header">
          <div>
            <h2 className="headline serif">{headline}</h2>
            <button className="cta-btn" onClick={() => navigate('/login')}>{ctaLabel} </button>
          </div>
          <div>
            <p className="header-copy">{description}</p>
          </div>
        </div>

        {/* Two large feature cards */}
        <div className="feature-grid">
          <div className="feature-card feature-card--media">
            <div className="media">
              <Win1/>
            </div>
            <div className="card-footer">
              <p className="card-footer-label">{mediaCardLabel}</p>
              <p className="card-footer-copy">{mediaCardCopy}</p>
            </div>
          </div>

          <div className="feature-card feature-card--panel">
            <div className="panel">
              <Win2/>
            </div>
            <div className="card-footer">
              <p className="card-footer-label">{panelCardLabel}</p>
              <p className="card-footer-copy">{panelCardCopy}</p>
            </div>
          </div>
        </div>

        {/* Dynamic 2-4 item info grid */}
        <div className="info-grid" style={{ ['--info-cols' as any]: cols }}>
          {infoItems.map((item) => (
            <div className="info-card" key={item.title}>
              <div className="info-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                >
                  {item.icon}
                </svg>
              </div>
              <p className="info-title">{item.title}</p>
              <p className="info-copy">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
